import { num, type ComponentForm } from '../component-types.js';

/**
 * `procgen` (engine/docs/systems/generators.md section 11.1): runs
 * procedural generators (`*.procgen.json`) around the viewers. `graph` lists
 * the root generators (inputs are pulled in transitively); point outputs
 * with a `spawn` block become prefab entities in Play. Edit generators in the
 * Procedural panel (Window > Procedural).
 */
const form: ComponentForm = {
  key: 'procgen',
  label: 'Procedural generation',
  icon: 'layers',
  order: 128,
  defaults: { graph: [], seed: 1 },
  fields: [
    {
      key: 'graph',
      label: 'Generators',
      type: { kind: 'json' },
      hint: 'Root generator paths, e.g. ["gen/nature.procgen.json"]. Their inputs are loaded automatically.',
    },
    {
      key: 'seed',
      label: 'World seed',
      type: num(1, { min: 0, integer: true }),
      hint: 'u64 world seed (1). procgen_regenerate(seed) can change it at run time.',
      read: (v) => v.seed ?? 1,
    },
    {
      key: 'params',
      label: 'Param overrides',
      type: { kind: 'json' },
      hint: '{ "<instance or path>": { "<param>": value } } over the generators\' defaults.',
    },
    {
      key: 'plane',
      label: 'Plane',
      type: { kind: 'enum', options: ['xz', 'xy'] },
      hint: 'Generator space → world: xz (3D, default) or xy (2D projects).',
    },
    {
      key: 'region',
      label: 'Region',
      type: { kind: 'json' },
      hint: '{ "type": "around", "radius": 400 } (viewers, default), { "type": "rect", "min": [x, z], "max": [x, z] } or { "type": "circle", ... }.',
    },
    {
      key: 'spawn',
      label: 'Spawn',
      type: { kind: 'json' },
      hint: 'true: every point output with a spawn block (default). false: none. Or bindings: [{ "points": "nature.trees", "distance": 180, "where": "...", "max": 5000, "mode": "play" | "edit" | "both" }].',
    },
    {
      key: 'interest',
      label: 'Interest',
      type: { kind: 'json' },
      hint: '[{ "outputs": ["biomes.zone"], "radius": 400 }]: prefetch for scripts without spawning.',
    },
    {
      key: 'editor',
      label: 'Edit mode',
      hint: 'Debug markers in the Scene view, and optional spawning while editing.',
      type: {
        kind: 'object',
        fields: [
          { key: 'markers', label: 'Markers', type: { kind: 'json' }, hint: 'Outputs drawn as markers, e.g. ["nature.trees"].' },
          { key: 'markerDistance', label: 'Marker distance', type: num(10, { min: 0 }), read: (v) => v.markerDistance ?? 300 },
          { key: 'maxMarkers', label: 'Max markers', type: num(100, { min: 0, integer: true }), read: (v) => v.maxMarkers ?? 20000 },
          { key: 'spawn', label: 'Spawn in edit mode', type: { kind: 'bool' } },
        ],
      },
    },
    {
      key: 'budget',
      label: 'Budget',
      hint: 'Timing only; never changes results.',
      type: {
        kind: 'object',
        fields: [
          { key: 'budgetMs', label: 'Budget (ms / frame)', type: num(0.5, { min: 0 }), read: (v) => v.budgetMs ?? 3 },
          { key: 'jobsPerFrame', label: 'Jobs per frame', type: num(1, { min: 0, integer: true }), read: (v) => v.jobsPerFrame ?? 4 },
          { key: 'maxJobs', label: 'Max jobs', type: num(1, { min: 1, integer: true }), read: (v) => v.maxJobs ?? 16 },
          { key: 'syncMisses', label: 'Sync misses', type: num(1, { min: 0, integer: true }), read: (v) => v.syncMisses ?? 8 },
          { key: 'spawnsPerFrame', label: 'Spawns per frame', type: num(1, { min: 1, integer: true }), read: (v) => v.spawnsPerFrame ?? 32 },
          { key: 'despawnsPerFrame', label: 'Despawns per frame', type: num(1, { min: 1, integer: true }), read: (v) => v.despawnsPerFrame ?? 64 },
          { key: 'maxEntities', label: 'Max entities', type: num(100, { min: 0, integer: true }), read: (v) => v.maxEntities ?? 10000 },
          { key: 'cacheMb', label: 'Cache (MB)', type: num(16, { min: 1, integer: true }), read: (v) => v.cacheMb ?? 512 },
        ],
      },
    },
    { key: 'prewarm', label: 'Prewarm', type: { kind: 'bool' }, read: (v) => v.prewarm ?? true, hint: 'Generate and spawn around the starting viewers before the first Play frame.' },
    { key: 'enabled', label: 'Enabled', type: { kind: 'bool' }, read: (v) => v.enabled ?? true },
  ],
};
export default form;
